import {AxiosResponse} from "axios";

import CoreApi from "@/src/modules/core/api";
import {axios, getParams} from "@/src/modules/core/lib";
import type {GetListResponse, GetResponse, ParamsOptions} from "@/src/modules/core/types";

import type {
  CorrectSafelistingRequest,
  CreateSafelistingRequest,
  ModifyDaysRequest,
  Safelisting,
  UpdateSafelistingRequest,
} from "../types";
import type {
  SafelistingAvailability,
  SafelistingAvailabilityParams,
} from "../types/safelisting-availability";

class SafelistingsApi extends CoreApi {
  createSafelisting(
    data: CreateSafelistingRequest,
  ): Promise<AxiosResponse<GetResponse<Safelisting>>> {
    return super.create("safelisting", data);
  }

  updateSafelisting(
    id: number,
    data: UpdateSafelistingRequest,
  ): Promise<AxiosResponse<GetResponse<Safelisting>>> {
    return super.update("safelisting", id, data);
  }

  getSafelistings(options: ParamsOptions): Promise<AxiosResponse<GetListResponse<Safelisting>>> {
    return super.search("safelisting", options);
  }

  getSafelisting(id: number): Promise<AxiosResponse<GetResponse<Safelisting>>> {
    return super.getById("safelisting", id);
  }

  deleteSafelisting(id: number): Promise<AxiosResponse<void>> {
    return super.delete("safelisting", id);
  }

  cancelSafelisting(id: number): Promise<AxiosResponse<GetResponse<Safelisting>>> {
    return super.post(`safelisting/${id}/cancel`, {});
  }

  correctSafelisting(
    id: number,
    data: CorrectSafelistingRequest,
  ): Promise<AxiosResponse<GetResponse<Safelisting>>> {
    return super.post(`safelisting/${id}/correct`, data);
  }

  modifyDays(
    id: number,
    data: ModifyDaysRequest,
  ): Promise<AxiosResponse<GetResponse<Safelisting>>> {
    return axios({
      url: `safelisting/${id}/modify-days`,
      method: "patch",
      data,
    });
  }

  getQrCode(id: number): Promise<AxiosResponse<GetResponse<unknown>>> {
    return this.get(`safelisting/${id}/qr-code`);
  }

  getAvailability(
    params: SafelistingAvailabilityParams,
  ): Promise<AxiosResponse<GetResponse<SafelistingAvailability>>> {
    const searchParams = new URLSearchParams();
    searchParams.append("property_id", String(params.property_id));
    searchParams.append("days", params.days.join(","));
    if (params.vehicle_id != null) {
      searchParams.append("vehicle_id", String(params.vehicle_id));
    }
    if (params.plate) {
      searchParams.append("plate", params.plate);
    }
    if (params.household_id != null) {
      searchParams.append("household_id", String(params.household_id));
    }
    return axios({url: "safelistings/availability", method: "get", params: searchParams});
  }

  getAccountability(
    options?: ParamsOptions,
    extras?: {household_id?: number; context_property_id?: number},
  ): Promise<AxiosResponse<GetResponse<unknown>>> {
    const params = getParams(options);
    if (extras?.household_id != null) {
      params.append("household_id", String(extras.household_id));
    }
    if (extras?.context_property_id != null) {
      params.append("context_property_id", String(extras.context_property_id));
    }
    return axios({url: "safelistings/accountability", method: "get", params});
  }

  getHistory(options?: ParamsOptions): Promise<AxiosResponse<GetResponse<unknown>>> {
    const params = getParams(options);
    return axios({url: "safelistings/history", method: "get", params});
  }
}

export const safelistingsApi = new SafelistingsApi();
